import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from './AuthContext';
import Spinner from "./Spinner";
import SuccessModal from './SuccessModal';
import ErrorModal from './ErrorModal';
import MobileHeader from './MobileHeader'
import PageHeader from './PageHeader';
import { useTheme } from './ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faFileAlt } from '@fortawesome/free-solid-svg-icons';

const AdminReports = () => {
    const { theme } = useTheme();
    const { currentUser } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [groupBy, setGroupBy] = useState('Card Number');
    const [sortBy, setSortBy] = useState('Transaction Day');
    const [viewFormat, setViewFormat] = useState('9');  // Default to CSV
    const [includeSummary, setIncludeSummary] = useState(false);
    const [includeProfit, setIncludeProfit] = useState(false);
    const [userReport, setUserReport] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [carrierName, setCarrierName] = useState('');

    const policies = useSelector(state => state.policies.activePolicies);

    const [cardNumberChecked, setCardNumberChecked] = useState(false);
    const [cardNumberValue, setCardNumberValue] = useState('');

    const [driverNameChecked, setDriverNameChecked] = useState(false);
    const [driverNameValue, setDriverNameValue] = useState('');

    const [driverIdChecked, setDriverIdChecked] = useState(false);
    const [driverIdValue, setDriverIdValue] = useState('');

    const [unitNumberChecked, setUnitNumberChecked] = useState(false);
    const [unitNumberValue, setUnitNumberValue] = useState('');

    const [policyIdChecked, setPolicyIdChecked] = useState(false);
    const [policyIdValue, setPolicyIdValue] = useState('');

    const [subFleetChecked, setSubFleetChecked] = useState(false);
    const [subFleetValue, setSubFleetValue] = useState('');

    const [countryChecked, setCountryChecked] = useState(false);
    const [countryValue, setCountryValue] = useState('85'); // Default to "USA"

    const [stateChecked, setStateChecked] = useState(false);
    const [stateValue, setStateValue] = useState('');

    const [cityChecked, setCityChecked] = useState(false);
    const [cityValue, setCityValue] = useState('');

    const [chainIdChecked, setChainIdChecked] = useState(false);
    const [chainIdValues, setChainIdValues] = useState('');

    const [itemChecked, setItemChecked] = useState(false);
    const [itemValue, setItemValue] = useState('');

    const handlePolicyChange = (event) => {
        console.log("Policies: ", policies);
        const selectedPolicyNumber = event.target.value;
        const convertedSelectedPolicyValue = parseInt(selectedPolicyNumber);
        console.log("Selected Policy Number:", convertedSelectedPolicyValue);
        setPolicyIdValue(selectedPolicyNumber);

        // Find the policy to get the company name or description
        const selectedPolicy = policies.find(policy => {
            console.log("Checking policy number:", policy.PolicyNumber); // Log for comparison
            return policy.PolicyNumber === convertedSelectedPolicyValue;
        });
        if (selectedPolicy) {
            console.log("Selected Policy:", selectedPolicy);
            setCarrierName(selectedPolicy.CompanyName || selectedPolicy.Description);
        } else {
            setCarrierName(''); // Reset if no policy is selected
        }
    };

    useEffect(() => {
        console.log("Carrier Name Updated:", carrierName);
    }, [carrierName]);


    const handleSubmit = async (event) => {
        setIsLoading(true);
        event.preventDefault();
        const userPermissions = {
            isAdmin: currentUser?.isAdmin,
            isContractAdmin: currentUser?.isContractAdmin,
            isPolicyAdmin: currentUser?.isPolicyAdmin,
            NetworkId: currentUser?.tenantId,
            policyNumber: currentUser?.policyNumber,
            ContractId: currentUser?.ContractId,
        };
        const formData = {
            startDate,
            endDate,
            currency,
            groupBy,
            sortBy,
            includeSummary,
            includeProfit,
            userReport,
            viewFormat,
            filters: [],
            userPermissions,
            carrierName,
        };

        // Add filters based on checked values
        if (cardNumberChecked) formData.filters.push({ field: 'Card Number', value: cardNumberValue });
        if (driverNameChecked) formData.filters.push({ field: 'Driver Name', value: driverNameValue });
        if (driverIdChecked) formData.filters.push({ field: 'Driver ID', value: driverIdValue });
        if (unitNumberChecked) formData.filters.push({ field: 'Unit', value: unitNumberValue });
        if (policyIdChecked) formData.filters.push({ field: 'Policy Number', value: policyIdValue });
        if (subFleetChecked) formData.filters.push({ field: 'Sub Fleet', value: subFleetValue });
        if (countryChecked) formData.filters.push({ field: 'Country', value: countryValue });
        if (stateChecked) formData.filters.push({ field: 'State', value: stateValue });
        if (cityChecked) formData.filters.push({ field: 'City', value: cityValue });
        if (chainIdChecked) formData.filters.push({ field: 'Chain Id', value: chainIdValues });
        if (itemChecked) formData.filters.push({ field: 'Item', value: itemValue });

        console.log('Form Data:', formData);

        try {
            const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/getAdminTransReport', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) throw new Error('Network response was not ok.');

            // Handle Blob data for file download
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'download';
            if (contentDisposition) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(contentDisposition);
                if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, '');
            }
            a.setAttribute('download', filename);
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
            setIsLoading(false);
            setShowSuccessModal(true);
        } catch (error) {
            setIsLoading(false);
            setShowErrorModal(true);
            console.error('Error during file download:', error);
        }
    };

    const handleUserReportChange = (checked) => {
        setUserReport(checked);
        setPolicyIdChecked(checked);
        setIncludeSummary(!checked ? includeSummary : false);
        setIncludeProfit(!checked ? includeProfit : false);
    };

    return (
        <>
            {isLoading && <Spinner />}
            {showSuccessModal && (
                <SuccessModal
                    message="The report has been successfully generated and downloaded."
                    onClose={() => setShowSuccessModal(false)}
                />
            )}
            {showErrorModal && (
                <ErrorModal
                    message="There was an error processing your request. Please try again."
                    onClose={() => setShowErrorModal(false)}
                />
            )}
            <div className='mobile-container mx-auto px-4 sm:px-6 lg:px-8'>
                <MobileHeader theme={theme} />
                <PageHeader
                    heading="Admin Transaction Reports"
                    context="Generate comprehensive transaction reports with advanced filtering options for administrative analysis"
                    icon={
                        <FontAwesomeIcon icon={faFileAlt} className="w-6 h-6" />
                    }
                />
                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Report Configuration Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                                </svg>
                                Report Configuration
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">Configure your report parameters and output format</p>
                        </div>
                        <div className="p-6 space-y-6">
                            {/* Date Range */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="startDate" className="block text-sm font-semibold text-gray-700 mb-2">
                                        Start Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        id="startDate"
                                        name="startDate"
                                        value={startDate}
                                        onChange={e => setStartDate(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="endDate" className="block text-sm font-semibold text-gray-700 mb-2">
                                        End Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        id="endDate"
                                        name="endDate"
                                        value={endDate}
                                        onChange={e => setEndDate(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                    />
                                </div>
                            </div>

                            {/* Report Options */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label htmlFor="currency" className="block text-sm font-semibold text-gray-700 mb-2">Currency</label>
                                    <select
                                        id="currency"
                                        name="currency"
                                        value={currency}
                                        onChange={e => setCurrency(e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                    >
                                        <option value="USD">USD/Gallons</option>
                                        <option value="CAD" disabled>CAD/Liters</option>
                                        <option value="MXN" disabled>MXN/Liters</option>
                                        <option value="ANY" disabled>Multicurrency</option>
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="groupBy" className="block text-sm font-semibold text-gray-700 mb-2">Group by</label>
                                    <select
                                        id="groupBy"
                                        name="groupBy"
                                        value={groupBy}
                                        onChange={e => setGroupBy(e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                    >
                                        <option value="cardNumber">Card Number</option>
                                        <option value="arNumber">AR Number</option>
                                        <option value="city">City</option>
                                        <option value="chainId">Chain ID</option>
                                        <option value="db">Direct Bill</option>
                                        <option value="driverID">Driver ID</option>
                                        <option value="driverName">Driver Name</option>
                                        <option value="locationId">Location ID</option>
                                        <option value="locationName">Location Name</option>
                                        <option value="stateProv">State/Province</option>
                                        <option value="subFleet">Sub Fleet</option>
                                        <option value="tranDate">Transaction Date</option>
                                        <option value="tripNumber">Trip Number</option>
                                        <option value="unit">Unit</option>
                                        <option value="odometer">Odometer</option>
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="sortBy" className="block text-sm font-semibold text-gray-700 mb-2">Sort by</label>
                                    <select
                                        id="sortBy"
                                        name="sortBy"
                                        value={sortBy}
                                        onChange={e => setSortBy(e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                    >
                                        <option value="tranDate">Transaction Date</option>
                                        <option value="cardNumber">Card Number</option>
                                        <option value="city">City</option>
                                        <option value="db">Direct Bill / Funded</option>
                                        <option value="DriverID">Driver ID</option>
                                        <option value="driverName">Driver Name</option>
                                        <option value="locationName">Location Name</option>
                                        <option value="stateProv">State/Province</option>
                                        <option value="unit">Unit</option>
                                        <option value="odometer">Odometer</option>
                                    </select>
                                </div>
                            </div>

                            {/* View Format */}
                            <div>
                                <label htmlFor="viewFormat" className="block text-sm font-semibold text-gray-700 mb-2">Output Format</label>
                                <select
                                    id="viewFormat"
                                    name="viewFormat"
                                    value={viewFormat}
                                    onChange={e => setViewFormat(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                >
                                    <option value="2" disabled>PDF</option>
                                    <option value="9">CSV</option>
                                    <option value="4" disabled>Excel</option>
                                    <option value="3" disabled>Text</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Report Options Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Report Options
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">Select additional options to include in your report</p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includeSummary"
                                    checked={includeSummary}
                                    onChange={(e) => setIncludeSummary(e.target.checked)}
                                    disabled={userReport}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="includeSummary" className="ml-3 block text-sm font-medium text-gray-900">
                                    Include Totals
                                </label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includeProfit"
                                    checked={includeProfit}
                                    onChange={(e) => setIncludeProfit(e.target.checked)}
                                    disabled={userReport}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="includeProfit" className="ml-3 block text-sm font-medium text-gray-900">
                                    Include Profit Information
                                </label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="userReport"
                                    checked={userReport}
                                    onChange={(e) => handleUserReportChange(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="userReport" className="ml-3 block text-sm font-medium text-gray-900">
                                    Generate User Report
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Advanced Filters Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                                </svg>
                                Advanced Filters
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">Apply specific filters to narrow down your report results (optional)</p>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                <div className="flex flex-col sm:flex-row items-center justify-between w-full">
                                    <div className="flex items-center mb-2 sm:mb-0 w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={cardNumberChecked}
                                            onChange={() => setCardNumberChecked(!cardNumberChecked)}
                                        />
                                        <label className="flex-grow">Card Number:</label>
                                    </div>
                                    <div className="flex items-center w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="text"
                                            placeholder='Complete Card Number Required'
                                            value={cardNumberValue}
                                            onChange={(e) => setCardNumberValue(e.target.value)}
                                            maxLength="21"
                                            disabled={!cardNumberChecked}
                                            className="border-gray-300 border rounded p-2 flex-grow w-full"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center justify-between w-full">
                                    <div className="flex items-center mb-2 sm:mb-0 w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={driverNameChecked}
                                            onChange={() => setDriverNameChecked(!driverNameChecked)}
                                        />
                                        <label className="flex-grow">Driver Name:</label>
                                    </div>
                                    <div className="flex items-center w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="text"
                                            value={driverNameValue}
                                            onChange={(e) => setDriverNameValue(e.target.value)}
                                            disabled={!driverNameChecked}
                                            className="border-gray-300 border rounded p-2 flex-grow w-full"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center justify-between w-full">
                                    <div className="flex items-center mb-2 sm:mb-0 w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={driverIdChecked}
                                            onChange={() => setDriverIdChecked(!driverIdChecked)}
                                        />
                                        <label className="flex-grow">Driver ID:</label>
                                    </div>
                                    <div className="flex items-center w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="text"
                                            value={driverIdValue}
                                            onChange={(e) => setDriverIdValue(e.target.value)}
                                            disabled={!driverIdChecked}
                                            className="border-gray-300 border rounded p-2 flex-grow w-full"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center justify-between w-full">
                                    <div className="flex items-center mb-2 sm:mb-0 w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={unitNumberChecked}
                                            onChange={() => setUnitNumberChecked(!unitNumberChecked)}
                                        />
                                        <label className="flex-grow">Unit Number:</label>
                                    </div>
                                    <div className="flex items-center w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="text"
                                            value={unitNumberValue}
                                            onChange={(e) => setUnitNumberValue(e.target.value)}
                                            disabled={!unitNumberChecked}
                                            className="border-gray-300 border rounded p-2 flex-grow w-full"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center justify-between w-full">
                                    <div className="flex items-center mb-2 sm:mb-0 w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={policyIdChecked}
                                            onChange={() => setPolicyIdChecked(!policyIdChecked)}
                                            disabled={userReport} // Disable checkbox if userReport is true
                                        />
                                        <label className="flex-grow">Policy Id:</label>
                                    </div>
                                    <div className="flex items-center w-full sm:w-1/2 px-2 sm:px-0">
                                        <select
                                            value={policyIdValue}
                                            onChange={handlePolicyChange}
                                            disabled={!policyIdChecked}
                                            required={userReport}
                                            className="border-gray-300 border rounded p-2 flex-grow w-full"
                                        >
                                            <option value="">Select a policy</option>
                                            {policies.map((policy) => (
                                                <option key={policy.ID} value={policy.PolicyNumber}>
                                                    {policy.PolicyNumber} - {policy.CompanyName || policy.Description}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center justify-between w-full">
                                    <div className="flex items-center mb-2 sm:mb-0 w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={subFleetChecked}
                                            onChange={() => setSubFleetChecked(!subFleetChecked)}
                                        />
                                        <label className="flex-grow">Sub fleet:</label>
                                    </div>
                                    <div className="flex items-center w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="text"
                                            value={subFleetValue}
                                            onChange={(e) => setSubFleetValue(e.target.value)}
                                            disabled={!subFleetChecked}
                                            className="border-gray-300 border rounded p-2 flex-grow w-full"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center justify-between w-full">
                                    <div className="flex items-center mb-2 sm:mb-0 w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={countryChecked}
                                            onChange={() => setCountryChecked(!countryChecked)}
                                        />
                                        <label className="flex-grow">Country:</label>
                                    </div>
                                    <div className="flex items-center w-full sm:w-1/2 px-2 sm:px-0">
                                        <select
                                            value={countryValue}
                                            onChange={(e) => setCountryValue(e.target.value)}
                                            disabled={!countryChecked}
                                            className="border-gray-300 border rounded p-2 flex-grow w-full"
                                        >
                                            <option value="85">United States</option>
                                            <option value="67">Canada</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center justify-between w-full">
                                    <div className="flex items-center mb-2 sm:mb-0 w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={stateChecked}
                                            onChange={() => setStateChecked(!stateChecked)}
                                        />
                                        <label className="flex-grow">State/Province:</label>
                                    </div>
                                    <div className="flex items-center w-full sm:w-1/2 px-2 sm:px-0">
                                        <select
                                            value={stateValue}
                                            onChange={(e) => setStateValue(e.target.value)}
                                            disabled={!stateChecked}
                                            className="border-gray-300 border rounded p-2 flex-grow w-full"
                                        >
                                            <option value="AB">Alberta</option>
                                            <option value="AZ">Arizona</option>
                                            <option value="AR">Arkansas</option>
                                            <option value="AL">Alabama</option>
                                            <option value="AK">Alaska</option>
                                            <option value="BC">British Columbia</option>
                                            <option value="CT">Connecticut</option>
                                            <option value="CO">Colorado</option>
                                            <option value="CA">California</option>
                                            <option value="DC">District of Columbia</option>
                                            <option value="DE">Delaware</option>
                                            <option value="FL">Florida</option>
                                            <option value="GA">Georgia</option>
                                            <option value="HI">Hawaii</option>
                                            <option value="IN">Indiana</option>
                                            <option value="IL">Illinois</option>
                                            <option value="ID">Idaho</option>
                                            <option value="IA">Iowa</option>
                                            <option value="KY">Kentucky</option>
                                            <option value="KS">Kansas</option>
                                            <option value="LA">Louisiana</option>
                                            <option value="MB">Manitoba</option>
                                            <option value="MT">Montana</option>
                                            <option value="MS">Mississippi</option>
                                            <option value="MO">Missouri</option>
                                            <option value="MN">Minnesota</option>
                                            <option value="MI">Michigan</option>
                                            <option value="ME">Maine</option>
                                            <option value="MD">Maryland</option>
                                            <option value="MA">Massachusetts</option>
                                            <option value="NL">Newfoundland</option>
                                            <option value="NB">New Brunswick</option>
                                            <option value="NU">Nunavut</option>
                                            <option value="NT">NW Territories</option>
                                            <option value="NS">Nova Scotia</option>
                                            <option value="NY">New York</option>
                                            <option value="NV">Nevada</option>
                                            <option value="NM">New Mexico</option>
                                            <option value="NJ">New Jersey</option>
                                            <option value="NH">New Hampshire</option>
                                            <option value="NE">Nebraska</option>
                                            <option value="ND">North Dakota</option>
                                            <option value="NC">North Carolina</option>
                                            <option value="ON">Ontario</option>
                                            <option value="OR">Oregon</option>
                                            <option value="OK">Oklahoma</option>
                                            <option value="OH">Ohio</option>
                                            <option value="PE">Prince Edward Island</option>
                                            <option value="PA">Pennsylvania</option>
                                            <option value="QC">Quebec</option>
                                            <option value="RI">Rhode Island</option>
                                            <option value="SK">Saskatchewan</option>
                                            <option value="SD">South Dakota</option>
                                            <option value="SC">South Carolina</option>
                                            <option value="TN">Tennessee</option>
                                            <option value="TX">Texas</option>
                                            <option value="UT">Utah</option>
                                            <option value="VT">Vermont</option>
                                            <option value="VA">Virginia</option>
                                            <option value="WA">Washington</option>
                                            <option value="WY">Wyoming</option>
                                            <option value="WV">West Virginia</option>
                                            <option value="WI">Wisconsin</option>
                                            <option value="YT">Yukon</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center justify-between w-full">
                                    <div className="flex items-center mb-2 sm:mb-0 w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={cityChecked}
                                            onChange={() => setCityChecked(!cityChecked)}
                                        />
                                        <label className="flex-grow">City:</label>
                                    </div>
                                    <div className="flex items-center w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="text"
                                            value={cityValue}
                                            onChange={(e) => setCityValue(e.target.value)}
                                            disabled={!cityChecked}
                                            className="border-gray-300 border rounded p-2 flex-grow w-full"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center justify-between w-full">
                                    <div className="flex items-center mb-2 sm:mb-0 w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={chainIdChecked}
                                            onChange={() => setChainIdChecked(!chainIdChecked)}
                                        />
                                        <label className="flex-grow">Chain ID:</label>
                                    </div>
                                    <div className="flex items-center w-full sm:w-1/2 px-2 sm:px-0">
                                        <select
                                            value={chainIdValues}
                                            onChange={(e) => setChainIdValues(e.target.value)}
                                            disabled={!chainIdChecked}
                                            className="border-gray-300 border rounded p-2 flex-grow w-full"
                                        >
                                            <option value="0">0 - Independents/Other</option>
                                            <option value="1">1 - Pilot / Flying J</option>
                                            <option value="2">2 - Loves</option>
                                            <option value="3">3 - TA / Petro</option>
                                            <option value="4">4 - Sapp Brothers</option>
                                            <option value="5">5 - Wilco</option>
                                            <option value="6">6 - Speedway / SuperAmerica</option>
                                            <option value="7">7 - QuikTrip</option>
                                            <option value="8">8 - Kangaroo / Pantry</option>
                                            <option value="9">9 - Petro Canada</option>
                                            <option value="10">10 - Imperial Oil / Esso</option>
                                            <option value="11">11 - Kwik Trip</option>
                                            <option value="12">12 - Quick Fuel</option>
                                            <option value="13">13 - Ambest</option>
                                            <option value="14">14 - CAT Scales</option>
                                            <option value="15">15 - ExxonMobil US OTR Network</option>
                                            <option value="16">16 - Quarles</option>
                                            <option value="17">17 - Maverik</option>
                                            <option value="18">18 - Irving 24</option>
                                            <option value="19">19 - Road Ranger</option>
                                            <option value="20">20 - NFN Retail</option>
                                            <option value="21">21 - NFN Cardlock</option>
                                            <option value="22">22 - Eclipse Advantage LLC</option>
                                            <option value="23">23 - Capstone</option>
                                            <option value="24">24 - Radsync</option>
                                            <option value="25">25 - SC Fuels</option>
                                            <option value="26">26 - Circle K</option>
                                            <option value="27">27 - RaceTrac</option>
                                            <option value="28">28 - Caseys</option>
                                            <option value="29">29 - GPM</option>
                                            <option value="30">30 - Allsup's / Yesway</option>
                                            <option value="101">101 - WEX NAF</option>
                                            <option value="201">201 - Retail Ultramar</option>
                                            <option value="202">202 - Retail Chevron</option>
                                            <option value="203">203 - Retail Pioneer</option>
                                            <option value="204">204 - Retail Couche-Tard</option>
                                            <option value="205">205 - Retail Fas Gas</option>
                                            <option value="206">206 - Retail RaceTrac</option>
                                            <option value="211">211 - Cardlock Chevron</option>
                                            <option value="212">212 - Cardlock Columbia Fuels</option>
                                            <option value="213">213 - Cardlock Bluewave</option>
                                            <option value="214">214 - Cardlock Pipeline</option>
                                            <option value="215">215 - Cardlock Ultramar</option>
                                        </select>

                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center justify-between w-full">
                                    <div className="flex items-center mb-2 sm:mb-0 w-full sm:w-1/2 px-2 sm:px-0">
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={itemChecked}
                                            onChange={() => setItemChecked(!itemChecked)}
                                        />
                                        <label className="flex-grow">Item:</label>
                                    </div>
                                    <div className="flex items-center w-full sm:w-1/2 px-2 sm:px-0">
                                        <select
                                            value={itemValue}
                                            onChange={(e) => setItemValue(e.target.value)}
                                            disabled={!itemChecked}
                                            className="border-gray-300 border rounded p-2 flex-grow w-full"
                                        >
                                            <option value="ACCE">ACCE - ACCESSORIAL</option>
                                            <option value="ADD ">ADD  - ADDITIVES</option>
                                            <option value="AMDS">AMDS - AVIATION MERCHANDISE</option>
                                            <option value="ANFR">ANFR - ANTI-FREEZE</option>
                                            <option value="APRO">APRO - AUTO PROPANE</option>
                                            <option value="ATOM">ATOM - AUTOMOTIVE</option>
                                            <option value="AVGS">AVGS - AVIATION GAS</option>
                                            <option value="BDSL">BDSL - BIO DIESEL</option>
                                            <option value="BEVR">BEVR - BEVERAGE</option>
                                            <option value="BPRP">BPRP - MISC PROPANE</option>
                                            <option value="BRAK">BRAK - BRAKES AND WHEELS</option>
                                            <option value="CADV">CADV - CASH ADVANCE</option>
                                            <option value="CLTH">CLTH - CLOTHING</option>
                                            <option value="COUP">COUP - COUPON</option>
                                            <option value="DEF">DEF  - DIESEL EXHAUST FLUID UREA</option>
                                            <option value="DEFC">DEFC - DEF CONTAINER</option>
                                            <option value="DELI">DELI - RESTAURANT/DELI</option>
                                            <option value="DSL">DSL  - DIESEL</option>
                                            <option value="DSLM">DSLM - MEXICO DIESEL</option>
                                            <option value="ELEC">ELEC - ELECTRONICS</option>
                                            <option value="EVCH">EVCH - EV CHARGING</option>
                                            <option value="FAX">FAX  - FAX</option>
                                            <option value="FURN">FURN - FURNACE OIL</option>
                                            <option value="GAS">GAS  - GASOLINE</option>
                                            <option value="GASM">GASM - MEXICO GAS MAGNA</option>
                                            <option value="GASP">GASP - Premium Gas Mexico</option>
                                            <option value="GROC">GROC - C-STORE/GROCERIES</option>
                                            <option value="HARD">HARD - HARDWARE/ACCESSORIES</option>
                                            <option value="HOTL">HOTL - HOTEL</option>
                                            <option value="IDLE">IDLE - IDLEAIRE</option>
                                            <option value="JET">JET  - JET FUEL</option>
                                            <option value="KERO">KERO - KEROSENE</option>
                                            <option value="LABR">LABR - LABOR</option>
                                            <option value="LMPR">LMPR - LUMPER</option>
                                            <option value="MDSL">MDSL - TAX EXEMPT DIESEL</option>
                                            <option value="MERC">MERC - MISCELLANEOUS MERCHANDISE</option>
                                            <option value="MGAS">MGAS - TAX EXEMPT GAS</option>
                                            <option value="MRFR">MRFR - MARKED RFR</option>
                                            <option value="NGAS">NGAS - NATURAL GAS</option>
                                            <option value="OIL">OIL  - OIL</option>
                                            <option value="OILC">OILC - OIL CHANGE</option>
                                            <option value="PARK">PARK - Parking</option>
                                            <option value="PART">PART - PARTS</option>
                                            <option value="PHON">PHON - PHONE TIME</option>
                                            <option value="PNT">PNT  - PAINT</option>
                                            <option value="PROP">PROP - PROPANE</option>
                                            <option value="RECP">RECP - RECAP</option>
                                            <option value="REPR">REPR - REPAIR SERVICE</option>
                                            <option value="REST">REST - RESTAURANT</option>
                                            <option value="RFND">RFND - REFUND</option>
                                            <option value="RFR">RFR  - REEFER</option>
                                            <option value="RFRM">RFRM - THERMO</option>
                                            <option value="SCAN">SCAN - TONS IMAGING</option>
                                            <option value="SCLE">SCLE - WEIGH SCALE FEE</option>
                                            <option value="SHWR">SHWR - SHOWER</option>
                                            <option value="SPLT">SPLT - OTHER METHOD OF PAYMENT</option>
                                            <option value="STAX">STAX - SALES TAX</option>
                                            <option value="TCHN">TCHN - TIRE CHAINS</option>
                                            <option value="TIRE">TIRE - TIRES/TIRE REPAIR</option>
                                            <option value="TOLL">TOLL - AMBASSADOR BRIDGE TOLL</option>
                                            <option value="TRAL">TRAL - TRAILER</option>
                                            <option value="TRPP">TRPP - TRIP PERMIT</option>
                                            <option value="ULSD">ULSD - ULTRA LOW SULFUR DIESEL</option>
                                            <option value="WASH">WASH - CAR WASH</option>
                                            <option value="WIFI">WIFI - FLEET WIFI BILLING</option>
                                            <option value="WROM">WROM - WASH ROOM</option>
                                            <option value="WWFL">WWFL - WINDSHIELD WASHER FLUID</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* Submit Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-6">
                            <div className="flex justify-center">
                                <button
                                    type="submit"
                                    className="btn-primary px-8 py-3 text-base font-medium"
                                >
                                    Generate Report
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};

export default AdminReports;
